import type { TierHero } from '../types';
import type { ScatterLayout } from './buildScatterPoints';
import {
  BAN_PICK_LOG_FLOOR_PERCENT,
  banPickAxisLog10,
  computeScatterAxisScales,
} from './buildScatterPoints';
import { tierBoundaryMidScores } from './tierBoundaryMidScores';

export const TIER_BOUNDARY_SAMPLE_STEPS = 80;

export function rawPlotPoint(
  logX: number,
  winPct: number,
  scales: NonNullable<ReturnType<typeof computeScatterAxisScales>>,
  layout: ScatterLayout
): { x: number; y: number } {
  const nx = scales.sameLogX ? 0.5 : (logX - scales.minLogX) / scales.spanLogX;
  const ny = scales.sameY ? 0.5 : (winPct - scales.minY) / scales.spanY;
  return {
    x: nx * layout.plotWidth,
    y: (1 - ny) * layout.plotHeight,
  };
}

export interface TierBoundarySampleData {
  xs: number[];
  /** 各境界の SVG y（上から S|A, A|B, …） */
  boundaryYs: number[][];
  w: number;
  h: number;
}

export function getTierBoundarySampleData(
  tierHeroes: TierHero[],
  beta: number,
  layout: ScatterLayout
): TierBoundarySampleData | null {
  const scales = computeScatterAxisScales(tierHeroes);
  if (!scales) return null;
  const mids = tierBoundaryMidScores(tierHeroes);
  if (mids.length === 0) return null;

  const pLo = Math.max(scales.banPickMin, BAN_PICK_LOG_FLOOR_PERCENT);
  const pHi = Math.max(scales.banPickMax, pLo * 1.0001);
  const logLo = Math.log10(pLo);
  const logHi = Math.log10(pHi);
  const n = TIER_BOUNDARY_SAMPLE_STEPS + 1;
  const xs: number[] = [];
  const boundaryYs = mids.map(() => [] as number[]);

  for (let i = 0; i < n; i++) {
    const t = i / TIER_BOUNDARY_SAMPLE_STEPS;
    const logP = logLo + t * (logHi - logLo);
    const P = 10 ** logP;
    const logX = banPickAxisLog10(P);
    const x = rawPlotPoint(logX, mids[0]! - beta * P, scales, layout).x;
    xs.push(x);
    for (let k = 0; k < mids.length; k++) {
      const winPct = mids[k]! - beta * P;
      boundaryYs[k]!.push(rawPlotPoint(logX, winPct, scales, layout).y);
    }
  }

  return {
    xs,
    boundaryYs,
    w: layout.plotWidth,
    h: layout.plotHeight,
  };
}
